/**
 * @fileoverview Content component.core
 */
'use strict';
// React Import
import React, { Component } from 'react';
import {
  BrowserRouter,
  Route,
  Link,
  Switch,
  Redirect,
  withRouter
} from 'react-router-dom';
import PropTypes from 'prop-types';
// Library Import
import _ from 'lodash';
// AppConfig Import
import AppConfig from '../../AppConfig';
// Core Import
import Loading from './Loading';
// Util Import
import * as ConstUtil from '../../util/ConstUtil';
import * as LogUtil from '../../util/LogUtil';

/**
 * Content共通コンポーネント
 *
 * @class Main
 * @extends {Component}
 */
class Content extends Component {
  constructor(props) {
    super(props);
    _.bindAll(
      this,
      'changeDateTime',
      'changeValue',
      'filterOptions',
      'changeRadioButton'
    );
  }

  /**
   * ローディング表示
   *
   * @memberof Content
   */
  showLoading() {
    Loading.show();
  }

  /**
   * ローディング非表示
   *
   * @memberof Content
   */
  hideLoading() {
    Loading.hide();
  }

  /**
   * 通信失敗時共通処理
   *
   * @param {Object} err エラー
   * @returns
   * @memberof Content
   */
  failureConnection(err) {
    this.hideLoading();
    // エラーが存在しなし場合
    if (_.isEmpty(err)) {
      this.commonConnectionError();
      return;
    }
    // エラーレスポンスが存在しない場合
    if (_.isEmpty(err.response)) {
      this.commonConnectionError();
      return;
    }
    // 認証エラーの場合
    if (
      err.response.status ===
      ConstUtil.Common.HTTP_STATUS_CODE_LIST.UNAUTHORIZED
    ) {
      this.authenticationError();
      return;
    }
    // メッセージ画面に遷移
    this.commonConnectionError();
  }

  /**
   * 共通エラー処理
   *
   * @memberof Content
   */
  commonConnectionError() {
    // メッセージ画面に遷移
    this.props.history.push(
      ConstUtil.Common.COMPONENT.MESSAGE.URL +
        this.createQueryParam([
          {
            key: 'main',
            value: 'COMMON.ID701'
          }
        ])
    );
  }

  /**
   * 認証エラー処理
   *
   * @memberof Content
   */
  authenticationError() {
    localStorage.clear();
    // ポータルサイト ログイン画面遷移
    window.location.href =
      AppConfig.httpScheme +
      AppConfig.portalDomain +
      AppConfig.appPortNumber +
      ConstUtil.Common.PORTAL_URL_LIST.LOGIN;
  }

  /**
   * 曜日Noと文言のリスト取得
   *
   * @returns
   * @memberof Content
   */
  getWeekList() {
    return [
      {
        no: ConstUtil.Common.WEEK_LIST[0],
        text: ConstUtil.Message.COMMON.ID509
      },
      {
        no: ConstUtil.Common.WEEK_LIST[1],
        text: ConstUtil.Message.COMMON.ID510
      },
      {
        no: ConstUtil.Common.WEEK_LIST[2],
        text: ConstUtil.Message.COMMON.ID511
      },
      {
        no: ConstUtil.Common.WEEK_LIST[3],
        text: ConstUtil.Message.COMMON.ID512
      },
      {
        no: ConstUtil.Common.WEEK_LIST[4],
        text: ConstUtil.Message.COMMON.ID513
      },
      {
        no: ConstUtil.Common.WEEK_LIST[5],
        text: ConstUtil.Message.COMMON.ID514
      },
      {
        no: ConstUtil.Common.WEEK_LIST[6],
        text: ConstUtil.Message.COMMON.ID515
      }
    ];
  }

  /**
   * ファイルダウンロード
   *
   * @param {Array} documentList ドキュメントリスト
   * @memberof Content
   */
  fileDownLoad(documentList) {
    for (let documentItem of documentList) {
      // ダウンロードリンク要素を作成
      let downloadElement = document.createElement('a');
      downloadElement.download = documentItem.fileKey;
      downloadElement.href = documentItem.url;
      // 要素をクリックしダウンロードを実行
      downloadElement.click();
    }
  }

  /**
   * 設定値取得
   *
   * @param {String} storageKey ストレージキー
   * @param {String} serachKey 検索キー
   * * @param {String} searchValue 検索値
   * @param {String} contentKey 内容
   * @memberof Content
   */
  getSettingContent(storageKey, serachKey, searchValue, contentKey) {
    let list = JSON.parse(localStorage.getItem(storageKey));
    if (_.isEmpty(list)) {
      return '';
    }
    let item = _.find(list, { [serachKey]: searchValue });
    if (_.isEmpty(item)) {
      return '';
    }
    return item[contentKey];
  }

  /**
   * 権限名名取得
   *
   * @param {*} id
   * @returns
   * @memberof Content
   */
  getAuthName(id) {
    let value = '';
    switch (id) {
      case ConstUtil.Common.AUTHORITY_LIST.SYSTEM_MANAGER:
        value = ConstUtil.Message.USER_COMMON.ID018;
        break;
      case ConstUtil.Common.AUTHORITY_LIST.CENTER_MANAGER:
        value = ConstUtil.Message.USER_COMMON.ID019;
        break;
      case ConstUtil.Common.AUTHORITY_LIST.BERTH_WORKER:
        value = ConstUtil.Message.USER_COMMON.ID020;
        break;
      case ConstUtil.Common.AUTHORITY_LIST.CONTRACTOR:
        value = ConstUtil.Message.USER_COMMON.ID021;
        break;
      case ConstUtil.Common.AUTHORITY_LIST.DRIVER:
      default:
        value = ConstUtil.Message.USER_COMMON.ID022;
        break;
    }

    return value;
  }

  /**
   * メールアドレスの入力チェック
   *
   * @param {String} mailAddress メールアドレス
   * @param {Boolean} isRequired 必須項目
   * @memberof Content
   */
  checkMailAddressInput(mailAddress, isRequired) {
    // 空チェック(必須項目の場合)
    if (isRequired) {
      if (_.isEmpty(mailAddress)) {
        return ConstUtil.Message.COMMON.ID601 + ConstUtil.Message.COMMON.ID702;
      }
    }
    // 入力値がある場合
    if (!_.isEmpty(mailAddress)) {
      if (
        !mailAddress.match(ConstUtil.Common.REGULAR_EXPRESSION_PATTERN.P006)
      ) {
        return ConstUtil.Message.COMMON.ID601 + ConstUtil.Message.COMMON.ID703;
      }
    }
    return '';
  }

  /**
   * センターID取得
   *
   * @returns
   * @memberof Content
   */
  getCenterId() {
    let centerInfo = localStorage.getItem(
      ConstUtil.Common.STORAGE_KEY.CENTER_INFO
    );
    if (_.isEmpty(centerInfo)) {
      return '';
    }
    return JSON.parse(centerInfo).id;
  }

  /**
   * センター名取得
   *
   * @returns
   * @memberof Content
   */
  getCenterName() {
    let centerInfo = localStorage.getItem(
      ConstUtil.Common.STORAGE_KEY.CENTER_INFO
    );
    if (_.isEmpty(centerInfo)) {
      return '';
    }
    return JSON.parse(centerInfo).name;
  }

  /**
   * 選択肢取得
   *
   * @param {String} storageKey ストレージキー
   * @returns
   * @memberof Content
   */
  getSelectOptions(storageKey) {
    let list = JSON.parse(localStorage.getItem(storageKey));
    let options = [];
    for (let item of list) {
      options.push({ value: item.id, text: item.name });
    }
    return options;
  }

  /**
   * 削除用チェックリストのクリア
   *
   * @memberof Content
   */
  clearCheckList() {
    this.setState({
      checkList: []
    });
  }

  /**
   * コンテントエリアのスタイル変更
   *
   * @param {Object} screenComponent 画面コンポーネント
   * @returns
   * @memberof Content
   */
  changeContentStyle(screenComponent) {
    // コンテントエリアの高さを更新
    let content = document.getElementById('content');
    if (_.isEmpty(content)) {
      return;
    }
    if (_.isEmpty(screenComponent)) {
      return;
    }
    // 高さをリセット
    this.resetContentHeight(content);
    // 割り当てエリアがコンテントエリアの高さを超える場合
    if (content.offsetHeight < screenComponent.offsetHeight) {
      content.style.height = '100%';
    }
  }

  /**
   * 高さをリセット
   *
   * @param {Object} content コンテントエリア
   * @memberof Content
   */
  resetContentHeight(content) {
    if (content.offsetHeight < window.innerHeight) {
      content.style.height =
        window.innerHeight -
        document.getElementsByTagName('header')[0].offsetHeight -
        document.getElementsByTagName('footer')[0].offsetHeight +
        'px';
    }
  }

  /**
   * クエリパラメータ作成
   *
   * @param {Array}} params パラメータリスト
   * @returns {String} クエリパラメータ文字列
   * @memberof Content
   */
  createQueryParam(params) {
    // 初期値
    let queryString = ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.QUESTION_MARK;
    // クエリパラメータ文字列生成
    for (let param of params) {
      queryString +=
        param.key +
        ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.EQUAL +
        btoa(encodeURIComponent(param.value)) +
        ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.AND;
    }
    // 最終の「&」を削除
    return queryString.slice(0, -1);
  }

  /**
   * クエリパラメータ取得(キー指定)
   *
   * @param {string} key
   * @returns {Array}  value
   * @memberof Content
   */
  getQueryParam(key) {
    let hash = window.location.hash;
    let index = hash.indexOf(
      ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.QUESTION_MARK
    );
    // hashにクエリパラメータが存在しない場合
    if (index === -1) {
      return [];
    }
    // '?'以降を取得
    let queryString = hash.slice(index + 1);
    // '&'で分割
    let stringArray = queryString.split(
      ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.AND
    );
    const str = _.find(stringArray, str => this.getQueryKey(str) === key);
    // 指定したパラメーターが存在しない場合
    if (_.isEmpty(str)) {
      return [];
    }
    return this.getQueryValue(str);
  }

  /**
   * クエリパラメータリスト取得
   *
   * @returns {Array}  クエリパラメータリスト
   * @memberof Content
   */
  getQueryParams() {
    let hash = window.location.hash;
    let index = hash.indexOf(
      ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.QUESTION_MARK
    );
    // hashにクエリパラメータが存在しない場合
    if (index === -1) {
      return [];
    }
    // '?'以降を取得
    let queryString = hash.slice(index + 1);
    // '&'で分割
    let stringArray = queryString.split(
      ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.AND
    );
    // 1パラメータを連想配列に変換
    let paramsArray = [];
    for (let string of stringArray) {
      paramsArray.push({
        key: this.getQueryKey(string),
        value: this.getQueryValue(string)
      });
    }
    return paramsArray;
  }

  /**
   * クエリパラメータのキー取得
   *
   * @param {String} param パラメータ
   * @returns {String} キー
   * @memberof Content
   */
  getQueryKey(param) {
    let index = param.indexOf(ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.EQUAL);
    if (index === -1) {
      return '';
    }
    return param.slice(0, index);
  }

  /**
   * クエリパラメータの値取得
   *
   * @param {String} param パラメータ
   * @returns {String} 値
   * @memberof Content
   */
  getQueryValue(param) {
    let index = param.indexOf(ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.EQUAL);
    if (index === -1) {
      return '';
    }
    return decodeURIComponent(atob(param.slice(index + 1)));
  }

  /**
   * メッセージ定義のキー取得
   *
   * @param {String} queryValue クエリパラメータの値
   * @returns {Array} MessageConstのキーリスト
   * @memberof Content
   */
  getMessageKeys(queryValue) {
    return queryValue.split(ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.PERIOD);
  }

  /**
   * 権限チェック
   *
   * @returns
   * @memberof Content
   */
  checkAuthority() {
    // 権限取得
    let authorityId = localStorage.getItem(
      ConstUtil.Common.STORAGE_KEY.AUTHORITY_ID
    );
    // 権限がない場合
    if (_.isEmpty(authorityId)) {
      // ポータルサイト ログイン画面遷移
      window.location.href =
        AppConfig.httpScheme +
        AppConfig.portalDomain +
        AppConfig.appPortNumber +
        ConstUtil.Common.PORTAL_URL_LIST.LOGIN;
      return;
    }
    // 現在のhashを取得
    let hash = window.location.hash;
    // クエリパラメータを含む場合
    let index = hash.indexOf(
      ConstUtil.Common.QUERY_PARAMETER_KEY_LIST.QUESTION_MARK
    );
    if (index !== -1) {
      hash = hash.replace(hash.slice(index), '');
    }
    // hashからCOMPONENTを特定
    let componentList = _.cloneDeep(ConstUtil.Common.COMPONENT);
    let targetComponent = _.find(componentList, { URL: hash });
    // 該当画面が存在しない場合
    if (_.isEmpty(targetComponent)) {
      // ログイン情報削除
      localStorage.clear();
      // ポータルサイト ログイン画面遷移
      window.location.href =
        AppConfig.httpScheme +
        AppConfig.portalDomain +
        AppConfig.appPortNumber +
        ConstUtil.Common.PORTAL_URL_LIST.LOGIN;
      return;
    }
    // 権限ありの場合
    if (targetComponent.AUTHORITY_ID.indexOf(authorityId) !== -1) {
      return;
    }
    // 権限なし
    // センター管理・バース作業員の場合
    if (
      authorityId === ConstUtil.Common.AUTHORITY_LIST.CENTER_MANAGER ||
      authorityId === ConstUtil.Common.AUTHORITY_LIST.BERTH_WORKER
    ) {
      this.props.history.push(ConstUtil.Common.COMPONENT.BOOKING_ASSIGN.URL);
      return;
    }
    // 配送業者・ドライバーの場合
    if (
      authorityId === ConstUtil.Common.AUTHORITY_LIST.CONTRACTOR ||
      authorityId === ConstUtil.Common.AUTHORITY_LIST.DRIVER
    ) {
      // ポータルサイト ポータルトップ画面遷移
      window.location.href =
        AppConfig.httpScheme +
        AppConfig.portalDomain +
        AppConfig.appPortNumber +
        ConstUtil.Common.PORTAL_URL_LIST.HOME;
      return;
    }
    // ログイン情報削除
    localStorage.clear();
    // ポータルサイト ログイン画面遷移
    window.location.href =
      AppConfig.httpScheme +
      AppConfig.portalDomain +
      AppConfig.appPortNumber +
      ConstUtil.Common.PORTAL_URL_LIST.LOGIN;
  }

  /**
   * チェックボックス選択肢取得
   *
   * @param {Array} itemList リスト
   * @returns
   * @memberof Content
   */
  getCheckSelectOptions(itemList, checkedList, idName) {
    if (_.isEmpty(itemList)) {
      return;
    }
    let options = [];
    for (let item of itemList) {
      let checked = false;
      let selectedId = _.filter(checkedList, { [idName]: Number(item.id) });
      // 選択済みの場合
      if (!_.isEmpty(selectedId)) {
        checked = true;
      }
      options.push({ id: Number(item.id), text: item.name, checked: checked });
    }
    return options;
  }

  /**
   * チェック状態を変更する
   *
   * @param {Object} event イベント
   * @param {String} stateName State名
   * @param {String} idName ID名
   * @returns
   * @memberof Content
   */
  changeChecked(event, stateName, idName) {
    if (_.isEmpty(event)) {
      return;
    }
    let id = event.target.id;
    if (_.isEmpty(id)) {
      return;
    }
    id = Number(id);
    // IDが整数でない場合
    if (!_.isInteger(id)) {
      return;
    }
    // stateをコピー
    let optionList = _.cloneDeep(this.state[stateName]);
    // チェックした場合
    if (event.target.checked) {
      optionList.push({
        [idName]: id
      });
      this.setState({
        [stateName]: optionList
      });
      return;
    }
    // チェックを解除したIDを削除
    optionList = _.reject(optionList, { [idName]: id });
    this.setState({
      [stateName]: optionList
    });
  }

  /**
   * 日時変更
   *
   * @param {Object} event イベント
   * @param {Object} inst mobiscrollインスタンス
   * @memberof Content
   */
  changeDateTime(event, inst) {
    this.setState({
      [inst.settings.id]: inst.getVal()
    });
  }

  /**
   * 入力値変更
   *
   * @param {Object} event イベント
   * @returns
   * @memberof Content
   */
  changeValue(event) {
    if (_.isEmpty(event)) {
      return;
    }
    if (_.isEmpty(event.target)) {
      return;
    }
    this.setState({
      [event.target.id]: event.target.value
    });
  }

  /**
   * 選択肢フィルター
   *
   * @param {Array} sourceList 元データ
   * @param {String} targetListName フィルター対象のリスト名
   * @param {String} serachKey 検索値
   * @param {String} serachTarget 検索対象
   * @memberof Content
   */
  filterOptions(sourceList, targetListName, serachKey, serachTarget) {
    // 入力値が含まれる選択肢をフィルター
    let filterList = sourceList.filter(function(item) {
      if (_.isEmpty(serachKey)) {
        return true;
      }
      if (item[serachTarget].indexOf(serachKey) >= 0) {
        return true;
      }
    });
    this.setState({
      [targetListName]: filterList
    });
  }

  /**
   * ラジオボタン変更
   *
   * @param {Object} event イベント
   * @memberof Content
   */
  changeRadioButton(event) {
    if (_.isEmpty(event)) {
      return;
    }
    if (_.isEmpty(event.target)) {
      return;
    }
    this.setState({
      [event.target.name]: event.target.value
    });
  }
}

Content.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  })
};
export default Content;



// WEBPACK FOOTER //
// ./src/js/component/core/Content.jsx